import {
  Button,
  Flex,
  ResponsiveModalContent,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
} from '@bridebook/aura';
import { IDropdownOption } from '@bridebook/ui';
import { getGuests } from '@bridebook/web/lib/guestlist/selectors';
import { interactedWithGuestCommsAction } from '@bridebook/web/lib/inbox/guests/analytics/actions';
import { mapGroupValueToAnalyticsLabel } from '@bridebook/web/lib/inbox/guests/analytics/analytics-helpers';
import { useCreateMessageFlow } from '@bridebook/web/lib/inbox/guests/create-message-flow/create-message-flow-context';
import {
  IGuestGroup,
  useGuestsGroups,
} from '@bridebook/web/lib/inbox/guests/create-message-flow/recipients/use-guests-groups';
import { getGuestsMap, isGuestWithEmail } from '@bridebook/web/lib/inbox/guests/guests-helpers';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddressCollectorBannerContainer } from './address-collector-banner-container';
import { MissingEmailsBanner } from './missing-emails-banner';
import { RecipientsGroups } from './recipients-groups';
import { UpdateRecipientEmailModal } from './update-recipient-email-modal';

export type GuestGroupDropdownOption = IDropdownOption<
  { groups: IGuestGroup[] },
  'events' | 'guestGroups' | 'rsvpStatus' | 'email'
>;

export const Recipients = () => {
  const { t } = useTranslation('inbox');
  const dispatch = useDispatch();
  const { goToPreviousStep, goToNextStep, updateFormData, formData } = useCreateMessageFlow();
  const guests = useSelector(getGuests);

  const contextGuestsMap = useMemo(
    () => getGuestsMap(formData.recipients.guests),
    [formData.recipients.guests],
  );

  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<string>>(
    () =>
      new Set(
        guests
          .filter((guest) => isGuestWithEmail(contextGuestsMap[guest.id]))
          .map((guest) => guest.id),
      ),
  );

  const handleSelect = useCallback((guestId: string, selected: boolean) => {
    setSelectedGuestIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(guestId);
      } else {
        newSet.delete(guestId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = () => {
    setSelectedGuestIds(new Set(guests.filter(isGuestWithEmail).map((guest) => guest.id)));
  };

  const isAllGuestsSelected = useMemo(
    () => guests.filter(isGuestWithEmail).every((guest) => selectedGuestIds.has(guest.id)),
    [guests, selectedGuestIds],
  );

  const handleDeselectAll = () => {
    setSelectedGuestIds(new Set());
  };

  const selectedGuestsCount = selectedGuestIds.size;

  const handleSubmit = () => {
    updateFormData({
      recipients: {
        guests: guests.filter((guest) => selectedGuestIds.has(guest.id)),
      },
    });
    goToNextStep();
    dispatch(
      interactedWithGuestCommsAction({
        action: 'clicked',
        location: 'guestComms',
        section: 'selectRecipients',
        element: 'continueButton',
        elementLabel: 'Continue to summary',
        numberOfGuests: selectedGuestsCount,
        guestGroup: mapGroupValueToAnalyticsLabel(groupingOption.value),
      }),
    );
  };

  const navigateBack = () => {
    updateFormData({
      recipients: {
        guests: guests.filter((guest) => selectedGuestIds.has(guest.id)),
      },
    });
    goToPreviousStep();
  };

  const { teamsByCategory, teamsByEvent, teamsByRsvpStatus, teamsByEmail } = useGuestsGroups();

  const groupingOptions = useMemo<GuestGroupDropdownOption[]>(
    () => [
      {
        label: t('guests.createMessageFlow.recipients.groupLabel.events'),
        value: 'events',
        groups: teamsByEvent,
      },
      {
        label: t('guests.createMessageFlow.recipients.groupLabel.guestGroups'),
        value: 'guestGroups',
        groups: teamsByCategory,
      },
      {
        label: t('guests.createMessageFlow.recipients.groupLabel.rsvpStatus'),
        value: 'rsvpStatus',
        groups: teamsByRsvpStatus,
      },
      {
        label: t('guests.createMessageFlow.recipients.groupLabel.emailAvailability'),
        value: 'email',
        groups: teamsByEmail,
      },
    ],
    [teamsByEvent, teamsByCategory, teamsByRsvpStatus, teamsByEmail, t],
  );
  const [groupingOptionIndex, setGroupingOptionIndex] = useState(0);
  const groupingOption = groupingOptions[groupingOptionIndex];

  return (
    <>
      <ResponsiveModalHeader onBackButtonClick={navigateBack}>
        {t('guests.createMessageFlow.recipients.header')}
      </ResponsiveModalHeader>
      <ResponsiveModalContent className="xl:pt-4">
        <div className="flex w-full flex-col gap-0 xl:gap-4 xl:px-[100px] xl:py-0">
          <AddressCollectorBannerContainer />
          <RecipientsGroups
            selectedGuestIds={selectedGuestIds}
            handleSelect={handleSelect}
            groupingOptions={groupingOptions}
            groupingOptionIndex={groupingOptionIndex}
            setGroupingOptionIndex={setGroupingOptionIndex}
          />
          <MissingEmailsBanner />
        </div>
      </ResponsiveModalContent>
      <Flex className="w-full items-center justify-between gap-3 border-t border-space-15 bg-white px-4 py-3 xl:px-[100px]">
        <div className="flex-1 text-sm font-semibold whitespace-nowrap text-space">
          {t('guests.createMessageFlow.recipients.guestCount', {
            count: selectedGuestsCount,
          })}
        </div>

        <Flex className="flex-wrap justify-end gap-3">
          <Button
            variant="tertiary"
            size="sm"
            onClick={handleSelectAll}
            disabled={isAllGuestsSelected}>
            {t('guests.createMessageFlow.recipients.selectAll')}
          </Button>

          <Button
            variant="tertiary"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedGuestsCount === 0}>
            {t('guests.createMessageFlow.recipients.deselectAll')}
          </Button>
        </Flex>
      </Flex>
      <ResponsiveModalFooter className="border-t border-space-15">
        <Button
          variant="primary"
          className="w-full max-w-[408px]"
          disabled={selectedGuestsCount === 0}
          onClick={handleSubmit}>
          {t('guests.createMessageFlow.recipients.submitButton')}
        </Button>
      </ResponsiveModalFooter>
      <UpdateRecipientEmailModal />
    </>
  );
};
